import DeleteIcon from '@mui/icons-material/Delete';
import FlipIcon from '@mui/icons-material/Flip';
import HideImageIcon from '@mui/icons-material/HideImage';
import PaletteIcon from '@mui/icons-material/Palette';
import WallpaperIcon from '@mui/icons-material/Wallpaper';

import { capture } from '@/handlers/error';
import { useOutsideClick } from '@/hooks/useOutsideClick';
import { ResizeBorder } from '@/pages/LessonEditor/components/ResizeBorder';
import { useComponentEditor, useSlideEditor } from '@/providers/editor/EditorProvider';
import { Coordinate, isSlideComponentDrawing, PropsWithPreview, Question, Size, SlideComponent } from '@/types/slide';
import { Box, Stack } from '@mui/material';
import { useNotifications } from '@toolpad/core';
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDebounce } from 'rooks';
import { ImagePicker } from '../inputs/ImagePicker/ImagePicker';
import { BUILTIN_IMG_SRC } from '../inputs/ImagePicker/source';
import { ResultDisplayDrawing } from '../results/ResultDisplayDrawing';
import { ComponentLabel } from './ComponentLabel';
import { ContextualBar } from './ContextualBar';
import { useRightClickMenu } from './hooks/useRightClickMenu';
import { useSlideComponent } from './hooks/useSlideComponent';
import { FrontendIcon } from './icons/FrontendIcon';
import { RightClickMenu } from './RightClickMenu';
import { SlideRef } from './Slide';

interface Props {
    ref: React.RefObject<HTMLDivElement | null>;
    init: {
        model: SlideComponent;
        focus?: boolean;
    };
    slideRef: React.RefObject<SlideRef | null>;
    question?: Question;
}

export const SlideComponentDrawing: React.FC<PropsWithPreview<Props>> = ({ ref, init, preview, slideRef, question }) => {
    const { t } = useTranslation();
    const notifications = useNotifications();
    const { selectedSlide } = useSlideEditor();
    const { deleteComponent, updateComponent, createSlideImage, bringToFront, bringToBack, copyComponent, pasteComponent, undo, redo } = useComponentEditor();

    const { rightClickMenu, handleRightClickMenu, rightClickMenuClose } = useRightClickMenu();

    // Add refs for the popovers
    const imagePickerRef = useRef<HTMLDivElement | null>(null);

    const { isSelected } = useSlideComponent(ref, [], !!preview);

    const [model, setModel] = useState<SlideComponent>({ ...init.model });
    const [isFocus, setIsFocus] = useState<boolean>(init.focus ?? false);

    const [tSize, setTSize] = useState<Size>(model.size);
    const [tPos, setTPos] = useState<Coordinate>(model.pos);

    const [aspectRatio] = useState(model.size.width / model.size.height);

    // Add state for image picker popover
    const [imagePickerAnchorEl, setImagePickerAnchorEl] = useState<HTMLElement | null>(null);

    const answer = question?.answers?.[0];
    const isEditing = isFocus || isSelected;

    const onDeleteComponent = React.useCallback(
        async (id: string) => {
            try {
                await deleteComponent(id);
            } catch (error) {
                notifications.show(error instanceof Error ? error.message : `${error}`, { severity: 'error' });
                capture(error);
            }
        },
        [deleteComponent, notifications],
    );

    const onUpdateComponent = React.useCallback(
        async (id: string, component: SlideComponent) => {
            try {
                await updateComponent(id, component);
            } catch (error) {
                notifications.show(error instanceof Error ? error.message : `${error}`, { severity: 'error' });
                capture(error);
            }
        },
        [updateComponent, notifications],
    );

    const onBringToFrontHandler = React.useCallback(
        async (id: string) => {
            try {
                await bringToFront(id);
            } catch (error) {
                notifications.show(error instanceof Error ? error.message : `${error}`, { severity: 'error' });
                capture(error);
            }
        },
        [bringToFront, notifications],
    );

    const onBringToBackHandler = React.useCallback(
        async (id: string) => {
            try {
                await bringToBack(id);
            } catch (error) {
                notifications.show(error instanceof Error ? error.message : `${error}`, { severity: 'error' });
                capture(error);
            }
        },
        [bringToBack, notifications],
    );

    const onUploadImage = React.useCallback(
        async (file: File): Promise<string> => {
            try {
                return await createSlideImage(selectedSlide.editorData?.id ?? '', file);
            } catch (error) {
                notifications.show(error instanceof Error ? error.message : `${error}`, { severity: 'error' });
                capture(error);
                return '';
            }
        },
        [createSlideImage, notifications, selectedSlide.editorData?.id],
    );

    const debouncedUpdateComponent = useDebounce(onUpdateComponent, 500);

    useOutsideClick(ref, () => {
        if (preview) return;
        if (!ref) return;

        setIsFocus(false);
    });

    if (!isSlideComponentDrawing(model)) {
        return null;
    }

    const isEmptyBackground = !model.content.imageURL && !model.content.image;

    return (
        <Box
            onContextMenu={isEditing ? handleRightClickMenu : undefined}
            ref={ref}
            id={`drawing-component-${model.id}`}
            sx={{
                position: 'absolute',
                left: tPos.x,
                top: tPos.y,
                m: 'inherit',
                p: 'inherit',
                textAlign: 'left',
                width: tSize.width,
                height: tSize.height,
            }}
            onClick={(e) => {
                if (!preview) {
                    e.stopPropagation();
                }
            }}
            onDoubleClick={(e) => {
                if (!preview) {
                    e.stopPropagation();
                    setImagePickerAnchorEl(ref.current);
                }
            }}
        >
            <ResizeBorder
                minSize={{ width: 400, height: 300 }}
                maxSize={{ width: 800, height: 600 }}
                size={tSize}
                pos={tPos}
                preview={preview}
                isSelected={isEditing}
                onResize={(size, pos) => {
                    setTSize(size);
                    setTPos(pos);
                    const updateModel = { ...model, size, pos };
                    setModel({ ...updateModel });
                    debouncedUpdateComponent?.(model.id, { ...updateModel });
                }}
                aspectRatio={aspectRatio}
                extraBoundary={{ right: 72, bottom: 0 }}
                slideRef={slideRef}
            >
                {!preview && (
                    <ComponentLabel
                        variant='caption'
                        sx={{
                            ...(!isEditing && {
                                opacity: 0, // Hide by default
                                transition: 'opacity 0.2s ease',
                                [`#drawing-component-${model.id}:hover &`]: {
                                    opacity: 1, // Show when text-component is hovered
                                },
                            }),
                        }}
                        noWrap
                    >
                        {t('slide.components.drawing.title')}
                    </ComponentLabel>
                )}

                <Box
                    sx={{
                        width: tSize.width,
                        height: tSize.height,
                        aspectRatio: 'auto',
                        transform: `scaleX(${model.content.flip ?? 1})`,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        border: 4,
                        borderColor: (theme) => (theme.vars || theme).palette.grey[500],
                        borderRadius: 1,
                        backgroundColor: 'white',
                    }}
                >
                    {!isEmptyBackground && (
                        <Box
                            component='img'
                            alt='image'
                            src={model.content.imageURL ? model.content.imageURL : `/asset/characters/${model.content.image}/image.png`}
                            sx={{
                                width: '100%',
                                height: '100%',
                                aspectRatio: 'auto',
                                objectFit: 'contain',
                            }}
                        />
                    )}
                    {answer && (
                        <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }}>
                            <ResultDisplayDrawing question={question} />
                        </Box>
                    )}
                </Box>

                <Stack sx={{ gap: 2, position: 'absolute', top: 0, right: 0 }}>
                    <FrontendIcon>
                        <PaletteIcon sx={{ width: 32, height: 32, color: (theme) => (theme.vars || theme).palette.grey[300] }} />
                    </FrontendIcon>

                    <FrontendIcon>
                        <Box component='img' src='/icons/EraserFilled.svg' sx={{ width: 40, height: 40 }} />
                    </FrontendIcon>

                    <FrontendIcon>
                        <DeleteIcon sx={{ width: 32, height: 32, color: (theme) => (theme.vars || theme).palette.grey[300] }} />
                    </FrontendIcon>
                </Stack>

                {!preview && isEditing && (
                    <ContextualBar
                        startButtons={[
                            {
                                icon: <WallpaperIcon />,
                                tooltip: t('slide.components.drawing.tooltip.edit'),
                                onClick: () => {
                                    setImagePickerAnchorEl(ref.current);
                                },
                            },
                            ...(!isEmptyBackground
                                ? [
                                      {
                                          icon: <FlipIcon />,
                                          tooltip: t('slide.components.drawing.tooltip.flip'),
                                          onClick: () => {
                                              const updatedContent = { ...model.content, flip: model.content.flip === 1 ? -1 : 1 };
                                              const updateModel = { ...model, content: updatedContent };
                                              setModel(updateModel);
                                              onUpdateComponent?.(model.id, updateModel);
                                          },
                                      },
                                      {
                                          icon: <HideImageIcon />,
                                          tooltip: t('slide.components.drawing.tooltip.removeBackground'),
                                          onClick: () => {
                                              const updatedContent = { ...model.content, image: '', imageURL: '' };
                                              const updateModel = { ...model, content: updatedContent };
                                              setModel(updateModel);
                                              onUpdateComponent?.(model.id, updateModel);
                                          },
                                      },
                                  ]
                                : []),
                        ]}
                        endButtons={[
                            {
                                icon: <DeleteIcon sx={{ color: (theme) => (theme.vars || theme).palette.error.main }} />,
                                tooltip: t('slide.components.drawing.tooltip.remove'),
                                onClick: () => {
                                    onDeleteComponent?.(model.id);
                                },
                            },
                        ]}
                    />
                )}

                {imagePickerAnchorEl && (
                    <ImagePicker
                        type='image'
                        title={t('slide.components.drawing.imagePicker.title')}
                        description={t('slide.components.drawing.imagePicker.description')}
                        librarySources={BUILTIN_IMG_SRC}
                        anchorEl={imagePickerAnchorEl}
                        slotProps={{
                            paper: {
                                ref: imagePickerRef, // Attach the ref to the Paper element
                            },
                        }}
                        value={{
                            value: model.content?.image,
                            imageURL: model.content?.imageURL,
                        }}
                        onClose={() => {
                            setImagePickerAnchorEl(null);
                            setIsFocus(false);
                        }}
                        onUpdate={(settings) => {
                            const updatedContent = { ...model.content, image: settings.value, imageURL: settings.imageURL };
                            const updateModel = { ...model, content: updatedContent };

                            setModel(updateModel);
                            onUpdateComponent?.(model.id, updateModel);
                        }}
                        onUpload={onUploadImage}
                    />
                )}
            </ResizeBorder>

            <RightClickMenu
                componentId={model.id}
                contextMenu={rightClickMenu}
                handleClose={rightClickMenuClose}
                bringToFront={onBringToFrontHandler}
                bringToBack={onBringToBackHandler}
                copyComponent={copyComponent}
                pasteComponent={pasteComponent}
                undo={undo}
                redo={redo}
            />
        </Box>
    );
};
